import React, { createContext, useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ApiResults } from '../../../models/ApiResults';
import { PaymentSheetListObj } from '../../../models/PaymentSheet';
import { TimeSheetListObj } from '../../../models/TimeSheet';
import { TimeSheetService } from '../../../services/TimeSheet.service';
import { hideSpinner, showSpinner } from '../../../store/slicers/globalSpinner.slicer';
import { showSnackbarAlert } from '../../../store/slicers/snackbarAlert.slicer';
import { checkResponseStatus } from '../../../utils/api/response';

export interface TimeSheetsFilterFields extends TimeSheetListObj {
  order_by?: string;
  page?: number;
  page_size?: number;
  search?: string;
}

export interface TimeSheetsResult extends ApiResults {
  results: PaymentSheetListObj[];
}

interface TimeSheetsContextProps {
  timeSheets: TimeSheetsResult | undefined;
  fetchTimeSheets: () => void;
  filterFields: TimeSheetsFilterFields;
  setFilterFields: React.Dispatch<React.SetStateAction<TimeSheetsFilterFields>>;
  page: number;
  rowsPerPage: number;
  handleOrderChange: (property: string, order: 'asc' | 'desc') => void;
  handlePageChange: (page: number) => void;
  handleOnChangeRowsPerPage: (page_size: number) => void;
}

const TimeSheetsPageContext = createContext<TimeSheetsContextProps | undefined>(undefined);

interface Props {
  children: React.ReactNode;
}

export const TimeSheetsPageContextProvider = ({ children }: Props) => {
  const dispatch = useDispatch();
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(5);

  const [timeSheets, setTimesheets] = useState<TimeSheetsResult | undefined>(undefined);
  const [filterFields, setFilterFields] = useState<TimeSheetsFilterFields>({} as TimeSheetsFilterFields);

  const fetchTimeSheets = async () => {
    dispatch(showSpinner());

    try {
      const response = await TimeSheetService.getTimeSheetList();
      if (checkResponseStatus(response)) {
        setTimesheets({
          results: response?.data,
          count: response?.data?.length,
          page_count: 1,
          page_number: 1
        });
      }
    } catch (error: any) {
      dispatch(
        showSnackbarAlert({ title: 'Erro', message: error?.data || 'Houve um erro ao processar a sua solicitação', severity: 'error' })
      );
    } finally {
      dispatch(hideSpinner());
    }
  };

  const handleOrderChange = (property: string, order: 'asc' | 'desc') => {
    const orderFilter = order === 'asc' ? property : `-${property}`;
    setFilterFields({ ...filterFields, order_by: orderFilter });
  };

  const handlePageChange = (page: number) => {
    setPage(page);
  };

  const handleOnChangeRowsPerPage = (page_size: number) => {
    setRowsPerPage(page_size);
  };

  useEffect(() => {
    fetchTimeSheets();
  }, [filterFields, page, rowsPerPage]);

  const contextValue: TimeSheetsContextProps = {
    timeSheets,
    fetchTimeSheets,
    filterFields,
    setFilterFields,
    page,
    rowsPerPage,
    handleOrderChange,
    handlePageChange,
    handleOnChangeRowsPerPage
  };

  return <TimeSheetsPageContext.Provider value={contextValue}>{children}</TimeSheetsPageContext.Provider>;
};

export const useTimeSheetsPageContext = () => {
  const context = useContext(TimeSheetsPageContext);
  if (!context) {
    throw new Error('useTimeSheetsPageContext must be used within a TimeSheetsPageContextProvider');
  }
  return context;
};
